import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { ReactNode } from 'react';
import { FileWithPath } from 'react-dropzone';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { DragNDrop } from '../atoms/drag-n-drop';
import { LoadingButton } from '../atoms/loading-button/loading-button';
import { DialogActions as DialogActionsInterface } from '../molecules/dialog';

interface UploadDialogProps<T> extends DialogActionsInterface {
  handleDownload?: () => void;
  onSubmit: (file: File) => Promise<T>;
  message?: ReactNode;
  title: string;
  templateId?: string;
  importId?: string;
}

interface FormValues {
  file: FileWithPath | null;
}
const defaultValues = {
  file: null,
};

function UploadDialog<T>({
  open,
  onCancel,
  onSubmit,
  handleDownload,
  message,
  title,
  templateId,
  importId,
}: UploadDialogProps<T>) {
  const { t } = useTranslation();

  const validationSchema = z.object({
    file: z.instanceof(File, {
      message: t('common:fileDropzone.requiredError'),
    }),
  });

  const {
    control,
    formState: { isLoading },
    handleSubmit,
    setValue,
    reset,
  } = useForm<FormValues>({
    defaultValues,
    mode: 'onChange',
    resolver: zodResolver(validationSchema),
  });

  const onConfirm = async (data: FormValues) => {
    if (!data.file) return;
    await onSubmit(data.file);
  };

  return (
    <Dialog
      onClose={onCancel}
      open={open}
      PaperProps={{
        sx: {
          width: '100%',
        },
      }}
      TransitionProps={{
        onExited: () => reset(defaultValues),
      }}>
      <DialogTitle
        sx={{
          alignItems: 'center',
          display: 'flex',
        }}>
        <Typography color="inherit" variant="inherit">
          {title}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {message}
        <Controller
          control={control}
          name="file"
          render={({ fieldState: { invalid, error }, field: { value } }) => (
            <>
              <DragNDrop
                onFile={file => {
                  setValue('file', file, { shouldValidate: true });
                }}
                file={value as FileWithPath}
              />
              {invalid && error ? (
                <Typography color="error.main" variant="subtitle2">
                  {error?.message}
                </Typography>
              ) : null}
            </>
          )}
        />
      </DialogContent>
      <DialogActions>
        <Button
          color="primary"
          onClick={onCancel}
          sx={{ color: 'text.primary' }}>
          {t('common:button.cancel')}
        </Button>
        {handleDownload && (
          <Button
            color="primary"
            onClick={handleDownload}
            variant="outlinedContained"
            id={templateId}>
            {t('common:downloadTemplate')}
          </Button>
        )}
        <LoadingButton
          color="primary"
          loading={isLoading}
          onClick={handleSubmit(onConfirm)}
          id={importId}
          variant="contained">
          {t('common:button.import')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

export default UploadDialog;
